/**
 * useCommandCenterFeed — Sprint 1 home-screen priority queue feed.
 *
 * Returns the ranked items for the chosen cadence ('today' or 'week') plus
 * the full response envelope (preset, role, read_only flag, etc).
 *
 * Refresh model (mirrors useNavCounts, intentional — same invalidation event):
 *   - on mount + cadence change
 *   - every 90 seconds (low traffic)
 *   - on `nexus:nav-counts-refresh` window event (after a write)
 *
 * Server-side cache is 60s; this hook's 90s poll lets the cache do most of
 * the work. The custom event fires immediately after mutations (record bill
 * payment, approve PTO, etc.) so users see the queue update without waiting.
 *
 * Returns all-empty for callers in local mode or with no active entity.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, isInLocalMode } from '@/lib/api';
import { useAppStore } from '@/stores/useAppStore';

export type CommandCenterCardType =
  | 'compliance'
  | 'collection'
  | 'payment'
  | 'renewal'
  | 'approval'
  | 'pto'
  | 'categorization'
  | 'checklist'
  | 'anomaly'
  | 'onboarding';

export interface CommandCenterItem {
  id: string;
  card_type: CommandCenterCardType;
  title: string;
  subtitle: string | null;
  amount_cents: number | null;
  due_date: string | null;
  days_until: number;
  owner_role: string | null;
  estimated_minutes: number | null;
  priority_score: number;
  urgency_tier?: 'overdue' | 'today' | 'this_week' | 'this_month' | 'future';
  click_route: string;
  source_table: string;
  source_id: string;
}

export interface CommandCenterFeed {
  cadence: 'today' | 'week';
  entity_id: string;
  preset: string;
  user_role: string;
  generated_at: string;
  items: CommandCenterItem[];
  total_items_considered: number;
  filtered_by_role: number;
  ranking_method: string;
  read_only?: boolean;
  cached?: boolean;
}

const EMPTY: CommandCenterFeed = {
  cadence: 'today',
  entity_id: '',
  preset: 'custom',
  user_role: '',
  generated_at: '',
  items: [],
  total_items_considered: 0,
  filtered_by_role: 0,
  ranking_method: 'urgency × cash_impact × inverse_effort',
};

const POLL_MS = 90_000;
const REFRESH_EVENT = 'nexus:nav-counts-refresh';

export function useCommandCenterFeed(
  cadence: 'today' | 'week' = 'today',
  limit?: number,
) {
  const { activeEntity } = useAppStore();
  const [feed, setFeed] = useState<CommandCenterFeed>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef(false);

  const refresh = useCallback(async () => {
    if (isInLocalMode() || !activeEntity?.id) {
      setFeed({ ...EMPTY, cadence });
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await api.getCommandCenterFeed(cadence, limit);
      if (!cancelRef.current) setFeed(r as CommandCenterFeed);
    } catch (err: unknown) {
      if (cancelRef.current) return;
      // Surface error for the consuming page to render an ErrorBanner (NS4 R3.3 pattern).
      // Specifically: an Employee role hitting this endpoint gets 403 + redirect:'/employee'
      // — App-level redirect handles that, but we expose `error` so the hook caller can
      // gate the UI appropriately if needed.
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to load Command Center';
      setError(message);
      setFeed({ ...EMPTY, cadence });
    } finally {
      if (!cancelRef.current) setLoading(false);
    }
  }, [activeEntity?.id, cadence, limit]);

  useEffect(() => {
    cancelRef.current = false;
    refresh();
    const id = setInterval(refresh, POLL_MS);
    const onEvent = () => refresh();
    window.addEventListener(REFRESH_EVENT, onEvent);
    return () => {
      cancelRef.current = true;
      clearInterval(id);
      window.removeEventListener(REFRESH_EVENT, onEvent);
    };
  }, [refresh]);

  return { feed, refresh, loading, error };
}

/** Convenience helper — call after a mutation that changes the queue. */
export function refreshCommandCenter() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(REFRESH_EVENT));
  }
}
